import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  ConsultantDailyReportConfig,
  NotificationCadence,
  NotificationCadenceQueryParams,
  PaginatedResponse,
  UpdateNotificationCadencePayload,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

export interface UpdateNotificationCadenceResponse {
  upsertedCount: number;
}

async function fetchNotificationCadencePage(
  page: number,
  limit: number,
  filters: Omit<NotificationCadenceQueryParams, 'page' | 'limit'> = {},
): Promise<{ count: number; rows: NotificationCadence[] }> {
  const response = await apiClient.get<ApiSuccess<{ count: number; rows: NotificationCadence[] }>>(
    '/admin/notification-cadence',
    { params: { ...filters, page, limit } },
  );
  return response.data.data;
}

export function useNotificationCadence(filters: NotificationCadenceQueryParams = {}) {
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 20;

  return useQuery({
    queryKey: queryKeys.notificationCadence.list(filters as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedResponse<NotificationCadence>> => {
      const payload = await fetchNotificationCadencePage(page, limit, filters);
      return {
        items: payload.rows,
        total: payload.count,
        page,
        limit,
        totalPages: Math.ceil(payload.count / limit),
      };
    },
  });
}

export function useNotificationCadenceCatalog() {
  return useQuery({
    queryKey: [...queryKeys.notificationCadence.all, 'catalog'] as const,
    queryFn: async (): Promise<NotificationCadence[]> => {
      const limit = 100;
      const firstPage = await fetchNotificationCadencePage(1, limit);
      const totalPages = Math.ceil(firstPage.count / limit);
      if (totalPages <= 1) return firstPage.rows;

      const remainingPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          fetchNotificationCadencePage(index + 2, limit),
        ),
      );
      return [firstPage, ...remainingPages].flatMap((pagePayload) => pagePayload.rows);
    },
  });
}

export function useUpdateNotificationCadence() {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: async (
      payload: UpdateNotificationCadencePayload,
    ): Promise<UpdateNotificationCadenceResponse> => {
      const response = await apiClient.patch<ApiSuccess<UpdateNotificationCadenceResponse>>(
        '/admin/notification-cadence',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationCadence.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationSettings.all });
    },
  });
}

const consultantDailyReportQueryKey = [
  ...queryKeys.notificationCadence.all,
  'consultant-daily-report',
] as const;

export function useConsultantDailyReportConfig() {
  return useQuery({
    queryKey: consultantDailyReportQueryKey,
    queryFn: async (): Promise<ConsultantDailyReportConfig> => {
      const response = await apiClient.get<ApiSuccess<ConsultantDailyReportConfig>>(
        '/admin/notification-cadence/consultant-daily-report',
      );
      return response.data.data;
    },
  });
}

export function useUpdateConsultantDailyReportConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (
      payload: ConsultantDailyReportConfig,
    ): Promise<ConsultantDailyReportConfig> => {
      const response = await apiClient.patch<ApiSuccess<ConsultantDailyReportConfig>>(
        '/admin/notification-cadence/consultant-daily-report',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consultantDailyReportQueryKey });
    },
  });
}
